import { useEffect } from "react";
import { Pencil, Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { PermState } from "@/contexts/fieldPermsEditor";

interface PermStateCellProps {
  fieldKey: string;
  /** Libellé lisible, lu sur le champ que la fiche allait rendre. */
  label: string;
  value: PermState;
  onChange: (state: PermState) => void;
  register: (fieldKey: string, label: string) => void;
  unregister: (fieldKey: string) => void;
  /** `false` masque l'option "Éditable" — mode préréglage, où l'édition n'est jamais un réglage disponible. */
  allowEdit?: boolean;
}

const ALL_OPTIONS: { state: PermState; icon: typeof Pencil; labelKey: string }[] = [
  { state: "edit", icon: Pencil, labelKey: "droits.etat.editable" },
  { state: "view", icon: Eye, labelKey: "droits.etat.visible" },
  { state: "hidden", icon: EyeOff, labelKey: "droits.etat.cache" },
];

/**
 * Remplace un champ de la fiche quand le mode « réglage des droits » est actif.
 *
 * Occupe la MÊME place que le champ qu'il remplace — même libellé au-dessus,
 * même hauteur de boîte, même padding `px-3 py-2` — pour que la fiche reste
 * reconnaissable au premier coup d'œil. C'est tout l'intérêt du mode : l'admin
 * règle les droits là où il a l'habitude de voir le champ, pas dans une liste
 * alphabétique de noms techniques.
 *
 * Trois états mutuellement exclusifs plutôt que deux cases à cocher : le couple
 * `can_see` / `can_edit` autorisait « modifiable mais invisible », qui n'a
 * aucun sens et que la RPC traite comme invisible.
 */
export function PermStateCell({ fieldKey, label, value, onChange, register, unregister, allowEdit = true }: PermStateCellProps) {
  const { t } = useTranslation();
  const OPTIONS = allowEdit ? ALL_OPTIONS : ALL_OPTIONS.filter((o) => o.state !== "edit");

  // La page apprend la liste des champs par le rendu de la fiche, pas par un
  // registre parallèle — c'est ce qui rend la dérive impossible. Le retrait au
  // démontage fait que l'ensemble enregistré vaut toujours « l'onglet visible ».
  useEffect(() => {
    register(fieldKey, label);
    return () => unregister(fieldKey);
  }, [fieldKey, label, register, unregister]);

  return (
    <div className="flex flex-col min-w-0">
      <span className="text-xs font-medium tracking-wider uppercase text-text-muted px-3 mb-1 truncate" title={label}>
        {label}
      </span>
      <div
        role="radiogroup"
        aria-label={label}
        className="flex items-stretch gap-1 px-1 py-1 bg-surface border border-solid border-border rounded-md"
      >
        {OPTIONS.map(({ state, icon: Icon, labelKey }) => {
          const active = value === state;
          return (
            <button
              key={state}
              type="button"
              role="radio"
              aria-checked={active}
              title={t(labelKey)}
              onClick={() => onChange(state)}
              className={cn(
                "flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-[11px] font-medium",
                "cursor-pointer outline-none shadow-none transition-colors min-w-0",
                active
                  ? "bg-accent text-white"
                  : "text-text-muted hover:bg-surface-2 hover:text-text-secondary",
              )}
            >
              <Icon size={12} className="shrink-0" />
              <span className="truncate">{t(labelKey)}</span>
            </button>
          );
        })}
      </div>
      {/* Le nom technique reste lisible : c'est lui qu'on retrouve dans une
          migration ou un message d'erreur de la RPC. */}
      <span className="text-[10px] text-text-muted/70 px-3 mt-0.5 truncate font-mono" title={fieldKey}>
        {fieldKey}
      </span>
    </div>
  );
}
